import { allowsAnyColour, resolvePaintedChoice } from '../../lib/paintedInvite'
import type { PaintedDef } from '../../lib/paintedInvite'
import ColourPicker from './ColourPicker'

/**
 * The colour choices a painted template can actually carry, as swatch rows:
 * the colours suggested for its type, then — depending on how the art is
 * made — its painted colourways, its papers, or a note that the painting or
 * photograph sets the background. Every row that is a flat colour ends in the
 * wildcard picker (ColourPicker), so the suggestions are a shortcut and never
 * a limit. An empty value means "the design's own", so choosing the first
 * swatch clears the override rather than storing it.
 */

type Props = {
  def: PaintedDef
  accent?: string | null
  background?: string | null
  onAccent: (hex: string) => void
  onBackground: (hex: string) => void
  appLang?: string
  compact?: boolean
  testPrefix?: string
}

const same = (a?: string | null, b?: string | null) => !!a && !!b && a.toLowerCase() === b.toLowerCase()

const PaintedColourRows = ({ def, accent, background, onAccent, onBackground, appLang = 'en', compact = false, testPrefix = 'painted' }: Props) => {
  const t = (en: string, ka: string) => (appLang === 'ka' ? ka : en)
  const choice = resolvePaintedChoice(def, accent, background)
  const free = allowsAnyColour(def)

  const rowLabel = 'mb-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-medium text-[#1b1033]/60 dark:text-gray-400'
  const note = 'font-normal text-[#4a3d66] dark:text-[#c3b8dd]/80'
  const dot = compact ? 'h-7 w-7' : 'h-9 w-9'
  const swatch = (on: boolean) =>
    `${dot} shrink-0 rounded-full border transition-all ${
      on
        ? 'ring-2 ring-offset-2 ring-[#1b1033]/40 dark:ring-white/80 ring-offset-white dark:ring-offset-[#130b26]'
        : 'border-black/10 hover:scale-105 dark:border-white/20'
    }`

  let backgroundRow = null
  if (def.colorways?.length) {
    const options = [
      { swatch: def.defaultSwatch || def.paper, label: t('Original', 'ორიგინალი'), key: null as string | null },
      ...def.colorways.map((c) => ({ swatch: c.swatch, label: appLang === 'ka' ? c.label.ka : c.label.en, key: c.key })),
    ]
    backgroundRow = (
      <div>
        <p className={rowLabel}>
          {t('Painting', 'ნახატი')}
          <span className={note}>{t('— the same design, painted in another colour', '— იგივე დიზაინი, სხვა ფერში')}</span>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {options.map((o) => (
            <button
              key={o.key || 'original'}
              type="button"
              onClick={() => onBackground(o.key ? o.swatch : '')}
              aria-pressed={choice.colorway === o.key}
              aria-label={o.label}
              title={o.label}
              className={swatch(choice.colorway === o.key)}
              style={{ backgroundColor: o.swatch }}
              data-testid={`${testPrefix}-colorway-${o.key || 'original'}`}
            />
          ))}
        </div>
      </div>
    )
  } else if (def.papers?.length) {
    const papers = def.papers
    const ownPaper = papers.some((p) => same(choice.paper, p))
    backgroundRow = (
      <div>
        <p className={rowLabel}>
          {t('Paper', 'ქაღალდი')}
          <span className={note}>
            {free
              ? t('— any colour works with one-ink drawings', '— ერთფეროვან ჩანახატს ნებისმიერი ფერი უხდება')
              : t('— tints that suit the flowers, or your own', '— ყვავილებს შეფერებული ტონები, ან შენი ფერი')}
          </span>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {papers.map((p, i) => (
            <button
              key={p}
              type="button"
              onClick={() => onBackground(i === 0 ? '' : p)}
              aria-pressed={same(choice.paper, p)}
              aria-label={t(`Paper ${p}`, `ქაღალდი ${p}`)}
              title={t(`Paper ${p}`, `ქაღალდი ${p}`)}
              className={swatch(same(choice.paper, p))}
              style={{ backgroundColor: p }}
              data-testid={`${testPrefix}-paper-${p}`}
            />
          ))}
          <ColourPicker
            value={choice.paper}
            onChange={onBackground}
            active={!ownPaper}
            label={{ en: 'paper', ka: 'ქაღალდის' }}
            appLang={appLang}
            compact={compact}
            testid={`${testPrefix}-paper-custom`}
          />
        </div>
      </div>
    )
  } else {
    backgroundRow = (
      <p className="text-[11px] leading-snug text-[#4a3d66] dark:text-[#c3b8dd]/80" data-testid={`${testPrefix}-background-fixed`}>
        {def.look === 'photo'
          ? t('The photograph sets the background — the colours above are taken from it.', 'ფონს ფოტო განსაზღვრავს — ზემოთ მოცემული ფერები მისგან არის აღებული.')
          : t('The painting sets the background — the colours above are taken from it.', 'ფონს ნახატი განსაზღვრავს — ზემოთ მოცემული ფერები მისგან არის აღებული.')}
      </p>
    )
  }

  const ownAccent = def.accents.some((a) => same(choice.accent, a))
  return (
    <div className={compact ? 'space-y-3' : 'space-y-5'} data-testid={`${testPrefix}-colours`}>
      <div>
        <p className={rowLabel}>
          {free ? t('Ink', 'მელანი') : t('Colour', 'ფერი')}
          <span className={note}>
            {free
              ? t('— drawings and words, any colour', '— ჩანახატი და ტექსტი, ნებისმიერი ფერი')
              : t('— suggested for this design, or your own', '— ამ დიზაინს შეფერებული, ან შენი ფერი')}
          </span>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {def.accents.map((a, i) => (
            <button
              key={a}
              type="button"
              onClick={() => onAccent(i === 0 ? '' : a)}
              aria-pressed={same(choice.accent, a)}
              aria-label={i === 0 ? t(`Colour ${a} — the design's own`, `ფერი ${a} — დიზაინის ფერი`) : t(`Colour ${a}`, `ფერი ${a}`)}
              title={i === 0 ? t("The design's own colour", 'დიზაინის ფერი') : t(`Colour ${a}`, `ფერი ${a}`)}
              className={swatch(same(choice.accent, a))}
              style={{ backgroundColor: a }}
              data-testid={`${testPrefix}-accent-${a}`}
            />
          ))}
          <ColourPicker
            value={choice.accent}
            onChange={onAccent}
            active={!ownAccent}
            label={free ? { en: 'ink', ka: 'მელნის' } : { en: 'type', ka: 'ტექსტის' }}
            appLang={appLang}
            compact={compact}
            testid={`${testPrefix}-accent-custom`}
          />
        </div>
      </div>
      {backgroundRow}
    </div>
  )
}

export default PaintedColourRows
